<svelte:window on:beforeunload={onBeforeUnload} />

{#if $planData.plan && !$planData.loading}
    <header class="plan-page__header" class:new-plan={config.newPlan}>
        {#if config.newPlan}
            <h1 class="plan-page__title plan-page__title--new-plan">
                <span class="plan-page__title-text">{i18n.createPlanTitle}</span>
            </h1>

            <div class="plan-page__new-plan-buttons">
                <Button
                    type="primary"
                    text={i18n.createPlan}
                    on:click={createPlan}
                    loading={createPlanInProgress}
                    disabled={createPlanInProgress}
                />
                <Button
                    type="secondary"
                    text={i18n.cancel}
                    on:click={cancelCreatePlan}
                    disabled={createPlanInProgress}
                />
            </div>
        {:else}
            <Link
                kind="standalone"
                iconClass="plan-page__title plan-page__title--back-link"
                iconPlacement="start"
                href={$backLink.url}
            >
                <span class="plan-page__title-text">{$backLink.label}</span>
                <span class="plan-page__title-text plan-page__title-text--short">{i18n.back}</span>
            </Link>

            {#if !config.isCourseTypeSelfPaced && config.canEdit && !isSelfPacedCourseOnePlanAtATime}
                <ButtonDropdown
                    arrow={false}
                    expanded={false}
                    iconClass="plan-page__menu-icon"
                    iconOnly={true}
                    id="plan-page-menu"
                    responsive={false}
                    items={settingsDropdownItems}
                    textOnly={false}
                    text={i18n.moreOptions}
                    type="plain"
                    on:selectedOption={onDropDownOptionSelected}
                />
            {/if}
        {/if}
    </header>

    {#if !config.isCourseTypeSelfPaced && showToFromDateTimePickerModal}
        <ToFromDateTimePickerModal
            {dateTimePickerModal}
            {datePickerFrom}
            dateValueFrom={$planData.plan.start}
            {hoursFrom}
            {minutesFrom}
            {isPMFrom}
            {datePickerTo}
            dateValueTo={$planData.plan.stop}
            {hoursTo}
            {minutesTo}
            {isPMTo}
            {dateTimeSet}
            on:clear={dateTimePickerClear}
            on:confirm={dateTimePickerConfirm}
            on:cancel={dateTimePickerCancel}
        />
    {/if}

    {#if showCreateTopicModal}
        <CreateTopicModal {createTopicModal} on:createTopic={createTopic} on:cancel={cancelTopic} />
    {/if}

    {#if showAddTopicModal}
        <AddTopicModal
            {addTopicModal}
            {moveResourcesCandidate}
            {initialTopicId}
            on:addTopic={addTopic}
            on:openCreateTopicModal={openCreateTopicModal}
            on:cancel={cancelTopic}
        />
    {/if}

    <MoveResourcesModal
        {showMoveResourcesModal}
        {selectedTopicId}
        {selectedTopicLabel}
        {initialTopicLabel}
        on:moveResources={moveElementsInTopicFolderForPlan}
        on:cancel={cancelMoveResources}
        />

    <Modal
        id="show-delete-plan-confirm-modal-id"
        bind:enabled={showDeletePlanConfirmModal}
        titleText={i18n.deletePlanConfirmDialog_Heading}
        bodyText={i18n.deletePlanConfirmDialog_Text}
        confirmText={i18n.confirm}
        cancelText={i18n.cancel}
        confirmButtonType="destructive"
        on:close={deletePlanClicked}
    />
    {#if config.canEdit}
        <div class="plan-page__feedback">
            <FilesBanner planId={$planData.plan.id} bind:isUploadStarted={isFilesUploadStarted} />
        </div>
    {/if}

    <Banner
        html={config.viewAsInCourseText}
        visible={config.isViewAsInCourseEnabled}
        closable={false}
    />

    <div class="plan-page__content">
        <div class="plan-page__side-bar">
            <PlanPageImage
                bind:imageUrl={$planData.plan.imageUrl}
                bind:imageDescription={$planData.plan.imageDescription}
                planId={$planData.plan.id}
                topic={$planData.plan.topic}
                canEdit={config.canEdit}
                {placeholderImageUrl}
            />

            {#if !config.isCourseTypeSelfPaced && $planData.plan.pinned && config.canEdit && !isSelfPacedCourseOnePlanAtATime}
                <div class="pinned-mark">{i18n.pinnedOnCourseOverview}</div>
            {/if}

            <div class="plan-page__title">
                <span id="plan-title-label" class="plan-page__property-label"
                    >{planTitleDisplayText}
                    {#if config.canEdit}
                        <span aria-hidden="true" class="field-required-star">*</span>
                    {/if}
                </span>
                {#if config.canEdit}
                    <textarea
                        class="plan-page__property-field"
                        placeholder={planTitlePlaceholderText}
                        bind:this={planTitleElement}
                        bind:value={$planData.plan.title}
                        on:input={(onTextAreaInput(
                            titleHandler,
                            $planData.plan.title,
                            planTitleElement
                        ),
                        checkIfEmptyTitle)}
                        aria-labelledby="plan-title-label"
                        on:keyup={checkIfEmptyTitle}
                        on:blur={onBlur}
                        on:focus={removeClass}
                    />
                {:else}
                    <div class="plan-page__property-text">
                        {$planData.plan.title || i18n.noTitle}
                    </div>
                {/if}
            </div>
            <div
                id="plan_title_required_field"
                role="alert"
                aria-live="assertive"
                class="field-required-star"
                style={showMandatoryFieldMessage ? "display: block;" : "display: none;"}
            >
                {warningMessage}
            </div>

            {#if isDateVisible(undefined, undefined, config.isCourseTypeSelfPaced)}
                <div class="plan-page__date">
                    <span class="plan-page__property-label">{dateElement}</span>
                    {#if config.canEdit}
                        <Button
                            type="plain"
                            htmlType="text"
                            CssClass="plan-page__date-button"
                            text={dateText}
                            on:click={openToFromDateTimePickerModal}
                        />
                    {:else}
                        <span class="plan-page__property-text">{dateText}</span>
                    {/if}
                </div>
            {/if}

            {#if isClassHoursVisible($planData.plan.classHours)}
                <div id="plan-class-hours-label" class="plan-page__class-hours">
                    <span class="plan-page__property-label">{classHoursDisplayText}</span>
                    {#if config.canEdit}
                        <input
                            class="plan-page__number-input"
                            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                            aria-labelledby="plan-class-hours-label"
                            bind:this={classHoursElement}
                            bind:value={$planData.plan.classHours}
                            on:input={onClassHoursUpdate(
                                classHoursHandler,
                                $planData.plan.classHours,
                                classHoursElement
                            )}
                        />
                        {#if isInvalidClassHoursMessageVisible}
                            <span class="plan-page__property-error-text"
                                >{invalidClassHoursMessage}</span
                            >
                        {/if}
                    {:else}
                        <span class="plan-page__property-text">{classHoursText}</span>
                    {/if}
                </div>
            {/if}

            {#if $planData.plan.topic || config.canEdit}
                <div class={planPageTopic}>
                    <span class="plan-page__property-label">{config.topicName}</span>
                    <div class="plan-page__topiclabel-topicremove">
                        {#if $planData.plan.topic}
                            <span class="plan-page__topiclabel" style={topicColorStyle}>
                                <a
                                    href={$planData.plan.topic.url}
                                    style={topicColorStyle}
                                    class="plan-page__topic-tag"
                                    rel="noopener"
                                >
                                    <span>{$planData.plan.topic.name}</span>
                                    <span class="screen-reader">{config.topicName}</span>
                                </a>
                            </span>
                            {#if config.canEdit}
                                <span>
                                    <button class="plan-page__topic-remove" on:click={removeTopic}>
                                        <span class="screen-reader">{config.removeTopicText}</span>
                                    </button>
                                </span>
                            {/if}
                        {:else if config.canEdit}
                            {#if config.isAnyTopicsInCourse}
                                <Button
                                    type="plain"
                                    text={config.addTopicText}
                                    on:click={openAddTopicModal}
                                />
                            {:else}
                                <Button
                                    type="plain"
                                    text={config.createTopicText}
                                    on:click={openCreateTopicModal}
                                />
                            {/if}
                        {/if}
                    </div>
                </div>
            {/if}
            <AssignTo
                enabled={config.canEdit && !config.publicAccessKey && config.assignToEnabled && config.isFolderForPlanCreated}
                elements={$planData.plan.elements}
                on:assignToChanged={assignToChanged}
            />
            <VisibilityToggle
                {config}
                plan={$planData.plan}
                {i18n}
                {setPlanActive}
                {getPlan}
                on:planActivationChanged={planActivationChanged}
            />
            {#if !config.canEdit && config.isLearner}
                <ProgressPlanLink
                    {backExtraParams}
                    containerClass="plan-page__progress-link"
                    courseId={config.courseId}
                    kpiContinue={config.kpi.kpiMeasurementContinueSinglePlanPage}
                    kpiStart={config.kpi.kpiMeasurementStartSinglePlanPage}
                    plan={$planData.plan}
                    useGenericResourceColumn={config.useGenericResourceColumn}
                />
            {/if}
        </div>
        <div class="plan-page__body">
            <PlanFields
                bind:plan={$planData.plan}
                {i18n}
                {config}
                {isParent}
                {numberOfPlansInCourse}
                {backExtraParams}
            />
        </div>
    </div>

    <div class="pin-plan-dialog">
        <Modal
            id="show-already-pinned-plan-modal-id"
            bind:enabled={showAlreadyPinnedPlanModal}
            titleText={i18n.anotherPlanIsPinnedHeading}
            bodyHtml={anotherPlanIsPinnedMessage}
            confirmText={i18n.anotherPlanIsPinnedConfirmation}
            cancelText={i18n.cancel}
            on:close={planPinningModuleClicked}
        />
    </div>
{/if}

<script>
    import { formatLanguageString } from '@itslearning/atlas/strings/format.js';
    import Banner from "@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte";
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import ButtonDropdown from "@itslearning/prometheus/assets/dropdowns/ButtonDropdown/v1/ButtonDropdown.svelte";
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import Link from "@itslearning/prometheus/assets/Navigation/Link/v1/Link.svelte";

    import AddTopicModal from "@its-ui/planner-common/components/AddTopicModal/AddTopicModal.svelte";
    import AssignTo from "@its-ui/planner-common/components/AssignTo/AssignTo.svelte";
    import CreateTopicModal from "@its-ui/planner-common/components/CreateTopicModal/CreateTopicModal.svelte";
    import MoveResourcesModal from "@its-ui/planner-common/components/MoveResourcesModal/MoveResourcesModal.svelte";
    import ToFromDateTimePickerModal from "@its-ui/planner-common/components/DateTimePicker/ToFromDateTimePickerModal.svelte";
    import VisibilityToggle from "@its-ui/planner-common/components/VisibilityToggle/VisibilityToggle.svelte";

    import FilesBanner from "../FilesBanner/FilesBanner.svelte";
    import PlanFields from "../PlanFields/PlanFields.svelte";
    import PlanPageImage from "../PlanPageImage/PlanPageImage.svelte";
    import ProgressPlanLink from "../../../../planner-common/components/ProgressPlanLink/ProgressPlanLink.svelte";

    import i18n from "@its-ui/planner-common/i18n";
    import { planData, backLink } from "@its-ui/planner-common/logic/planPage/stores";
    import { tableViewMode } from "@its-ui/planner-common/logic/tableView/constants";
    import {
        getPlan,
        getPinnedPlan,
        updatePlanPinned,
        dragAndDropPlanInLesson,
        getPlanLastInOrder,
        getAllPlanCounts,
        getAllPlanCountsPublic,
        connectToTopic
    } from "@its-ui/planner-common/api";
    import {
        updatePlanTitle,
        updatePlanDates,
        updatePlanClassHours,
        setPlanActive,
        createNewTopic,
        moveElementsInFolderForPlan,
        getTopicForPlan,
        removeTopicFromPlan,
        removePlanFromCourse,
        setTopicFolderForPlanFolder
    } from "@its-ui/planner-common/controllers/updatePlanController.js";
    import config from "@its-ui/planner-common/config";
    import { planType } from "@its-ui/planner-common/constants";
    import {
        getDateRangeString,
        getCurrentHours,
        getCurrentMinutes,
        isPM
    } from "@its-ui/planner-common/helpers/dateHelper";
    import { adjustToTimezone } from "@its-ui/planner-common/helpers/loadPlansHelper";
    import UpdateHandler from "@its-ui/planner-common/helpers/UpdateHandler";
    import {
        defaultFromHour,
        defaultToHour,
        plannerFieldType,
        maxTitleLength,
        TABLE_VIEW_MODE_QUERY_STRING_PARAMETER,
        TOPIC_ID_DESTINATION_QUERY_STRING_PARAMETER
    } from "@its-ui/planner-common/constants";
    import { postNewPlannerKpi } from "@its-ui/planner-common/helpers/kpiHelper";
    import { createPlaceholderImagesProvider } from "@its-ui/planner-common/helpers/planImageHelper";
    import {
        isDateVisible,
        isClassHoursVisible,
        getInvalidClassHoursMessageVisibility
    } from "@its-ui/planner-common/helpers/visibilityHelper";
    import {
        getAllQueryStringParametersFromCurrentUrl,
        hasParam,
        setParam,
        getParam
    } from "@its-ui/planner-common/helpers/backLinkHelper";
    import { handleTopicConnection } from "@its-ui/planner-common/helpers/moveResourcesHelper";

    import { onMount } from "svelte";

    export let isParent = config.childId === 0 ? false : true;
    let numberOfPlansInCourse;

    let planTitleElement;
    let planTitleDisplayText;
    let planTitlePlaceholderText;
    let classHoursElement;
    let classHoursDisplayText;
    let isInvalidClassHoursMessageVisible = false;
    let invalidClassHoursMessage;
    let showToFromDateTimePickerModal = false;
    let showCreateTopicModal = false;
    let showAddTopicModal = false;
    let showMoveResourcesModal = false;
    let createPlanInProgress = false;
    let showDeletePlanConfirmModal = false;
    let showAlreadyPinnedPlanModal = false;
    let isFilesUploadStarted = false;
    let connectedToEvent = false;
    let pinnedPlan;
    let anotherPlanIsPinnedMessage;
    let isTitleEmpty = true;
    let selectedTopicId = 0;
    let selectedTopicLabel = "";
    let initialTopicId = 0;
    let initialTopicLabel = "";
    let initialTopicIdLoaded = false;

    // Update handlers
    let titleHandler;
    let classHoursHandler;
    let datesDirty = false;
    let lastTopic;

    let showMandatoryFieldMessage = false;
    let warningMessage;
    let isPlanDeleted = false;
    let backExtraParams = null;

    const isSelfPacedCourseOnePlanAtATime = config.isNewPlannerBetterProgressionBetweenPlansEnabled && config.planType === planType.onePlanAtATime;

    const timeNow = Date.now();

    const dateTimePickerModal = "date-time-picker-modal";
    const datePickerFrom = "date-picker-from";
    const datePickerTo = "date-picker-to";

    const createTopicModal = "create-topic-modal";
    const addTopicModal = "add-topic-modal";

    const planPageTopic = "plan-page__topic";
    const planPageDate = "plan-page__date";

    const plansTextAreaElement = "#plans textarea";
    const plansPageTitleElement = "div.plan-page__title";
    const plansPageTitleClass = "plan-title-element-required";

    const placeholderImagesProvider = createPlaceholderImagesProvider(
        config.topicColours,
        config.defaultPlanImages
    );

    // Once the title is loaded set its height to match the content.
    $: if (planTitleElement !== undefined) {
        setTimeout(() => textAreaInputResize(planTitleElement), 0);
    }

    $: if (classHoursElement !== undefined) {
        setTimeout(() => textAreaInputResize(classHoursElement), 0);
    }

    $: topicColorStyle =
        $planData.plan && $planData.plan.topic
            ? `background-color: ${$planData.plan.topic.color};`
            : "";
    $: dateText =
        $planData.plan && $planData.plan.start && $planData.plan.stop
            ? getDateRangeString($planData.plan.start, $planData.plan.stop, config.locale, true)
            : config.canEdit
              ? i18n.addDate
              : i18n.noDate;

    $: classHoursDisplayText =
        $planData.plan != undefined &&
        $planData.plan.fields.find(f => f.type === plannerFieldType.ClassHours) != undefined
            ? $planData.plan.fields.find(f => f.type === plannerFieldType.ClassHours).headingText
            : i18n.classHours;
    $: planTitleDisplayText =
        $planData.plan != undefined &&
        $planData.plan.fields.find(f => f.type === plannerFieldType.PlanTitle) != undefined
            ? $planData.plan.fields.find(f => f.type === plannerFieldType.PlanTitle).headingText
            : i18n.title;
    $: planTitlePlaceholderText = i18n.addTitleWithPlaceholder.replace("{0}", planTitleDisplayText);
    $: invalidClassHoursMessage = i18n.enterNumberBetween.replace("{0}", "0").replace("{1}", "100");
    $: classHoursText = "0";

    $: if ($planData.plan && $planData.plan.classHours) {
        classHoursText = $planData.plan.classHours;
        isInvalidClassHoursMessageVisible = getInvalidClassHoursMessageVisibility(
            $planData.plan.classHours
        );
    }

    $: dateElement =
        $planData.plan != undefined &&
        $planData.plan.fields.find(f => f.type === plannerFieldType.Date) != undefined
            ? $planData.plan.fields.find(f => f.type === plannerFieldType.Date).headingText
            : i18n.dateHeader;

    // The variables containing date & time
    // If no date is set, there will be a placeholder text in date picker
    // If no time is set, default time selection will be 08-16 or 08 AM-04 PM if using 12h format
    $: hoursFrom =
        $planData.plan && $planData.plan.start
            ? getCurrentHours($planData.plan.start, config.useTwelveHourFormat)
            : defaultFromHour;
    $: minutesFrom =
        $planData.plan && $planData.plan.start ? getCurrentMinutes($planData.plan.start) : "00";
    $: isPMFrom =
        config.useTwelveHourFormat && $planData.plan && $planData.plan.start
            ? isPM($planData.plan.start)
            : false;

    $: hoursTo =
        $planData.plan && $planData.plan.stop
            ? getCurrentHours($planData.plan.stop, config.useTwelveHourFormat)
            : defaultToHour;
    $: minutesTo =
        $planData.plan && $planData.plan.stop ? getCurrentMinutes($planData.plan.stop) : "00";
    $: isPMTo =
        config.useTwelveHourFormat && $planData.plan && $planData.plan.stop
            ? isPM($planData.plan.stop)
            : false;

    $: dateTimeSet = $planData.plan && $planData.plan.start ? true : false;

    // Create update handlers for plan properties when the plan is loaded or the loaded plan changes
    $: if ($planData.plan && !$planData.loading) {
        if (!titleHandler) {
            titleHandler = new UpdateHandler({
                value: $planData.plan.title,
                save: v => updatePlanTitle($planData.plan.id, v),
                active: true
            });
        }
    }

    $: if ($planData.plan && !$planData.loading) {
        if (!classHoursHandler) {
            classHoursHandler = new UpdateHandler({
                value: $planData.plan.classHours,
                save: v => updatePlanClassHours($planData.plan.id, v),
                active: true
            });
        }
    }

    $: if ($planData.isNewFilesUploadStarted) {
        isFilesUploadStarted = true;
        $planData.isNewFilesUploadStarted = false;
    }

    // For visibility section
    let isFirstPageLoad = true;

    $: if ($planData.plan && !$planData.loading && config.newPlan && isFirstPageLoad) {
        $planData.plan.isActive = setVisibilityOnCreateNewPlan();
    }

    $: {
        if ($planData.plan) {
            const topic = $planData.plan.topic;

            if (!lastTopic || !topic || lastTopic.id !== topic.id) {
                lastTopic = topic;
            }
        }
    }

    $: placeholderImageUrl = placeholderImagesProvider.getImageForTopic(lastTopic);

    $: settingsDropdownItems = [
        {
            options: [
                {
                    iconCssClass: "plan-page__pin-plan-dropdown-icon",
                    label: $planData.plan && $planData.plan.pinned ? i18n.unpinPlan : i18n.pinPlan,
                    link: false,
                    iconsize: 24
                }
            ]
        }
    ];

    $: numberOfPlanElements = $planData.plan ? $planData.plan.elements.length : 0;

    $: if ($planData.plan && !$planData.loading && !initialTopicIdLoaded) {
        initialTopicId = $planData.plan.topic ? $planData.plan.topic.id : 0;

        if (initialTopicId > 0) {
            initialTopicLabel = $planData.plan.topic.name;
        }

        initialTopicIdLoaded = true;
    }

    $: moveResourcesCandidate = config.createFolderForEachNewTopics && numberOfPlanElements > 0;

    onMount(async () => {
        const result = config.publicAccessKey ? await getAllPlanCountsPublic() : await getAllPlanCounts();

        numberOfPlansInCourse = result ? parseInt(result.allPlansCount) : 0;
        backExtraParams = getAllQueryStringParametersFromCurrentUrl();
    });

    function cancelCreatePlan() {
        showDeletePlanConfirmModal = true;
    }

    function onClassHoursUpdate(handler, planDefault, element) {
        if (getInvalidClassHoursMessageVisibility(element.value)) {
            isInvalidClassHoursMessageVisible = true;

            return;
        }

        isInvalidClassHoursMessageVisible = false;
        onTextAreaInput(handler, planDefault, element);
    }

    function onTextAreaInput(handler, planDefault, element) {
        handler.update(planDefault);
        textAreaInputResize(element);
    }

    // On input to the non-html description text area re-calculate it's height so the content always fits.
    function textAreaInputResize(element) {
        if (element instanceof Element || element instanceof Document) {
            element.style.height = element.scrollHeight + "px";
        }
    }

    // Fired when the window is being closed or the user is navigating away
    function onBeforeUnload(event) {
        // Check update handlers to see if any have unsaved changes
        if (!isPlanDeleted && unsavedChanges()) {
            // Prompt the user there may be unsaved changes
            event.preventDefault();
            event.returnValue = "";
        }
    }

    function unsavedChanges() {
        return datesDirty || (titleHandler && titleHandler.isDirty);
    }

    // Show the datetime picker modal
    function openToFromDateTimePickerModal() {
        showToFromDateTimePickerModal = true;
    }

    async function dateTimePickerClear() {
        $planData.plan.start = undefined;
        $planData.plan.stop = undefined;

        if (!config.newPlan) {
            datesDirty = true;

            await updatePlanDates(
                $planData.plan.id,
                $planData.plan.start,
                $planData.plan.stop,
                connectedToEvent
            );

            datesDirty = false;

            // Set ordering of the edited plan to bottom if no date set
            if ($planData.plan.id > 1) {
                const target = await getPlanLastInOrder();

                const sourcePlan = $planData.plan;

                if (sourcePlan.id && target.PlanId) {
                    await dragAndDropPlanInLesson(
                        sourcePlan.id,
                        target.PlanId,
                        sourcePlan.topic ? sourcePlan.topic.id : null,
                        true
                    );
                }
            }
        }
    }

    async function dateTimePickerConfirm({ detail }) {
        if (detail.newStartDate) {
            $planData.plan.start = new Date(detail.newStartDate);
        }

        if (detail.newStopDate) {
            $planData.plan.stop = new Date(detail.newStopDate);
        }

        connectedToEvent = detail.connectedToEvent;

        if (!config.newPlan) {
            datesDirty = true;

            await updatePlanDates(
                $planData.plan.id,
                $planData.plan.start,
                $planData.plan.stop,
                connectedToEvent,
                detail.activityIds
            );

            datesDirty = false;

            if (connectedToEvent) {
                await updateWithEventDates($planData.plan.id);
            }

            await updatePlanVisibility($planData.plan.id);
        } else {
            if (connectedToEvent) {
                await updatePlanDates(
                    $planData.plan.id,
                    $planData.plan.start,
                    $planData.plan.stop,
                    connectedToEvent,
                    detail.activityIds
                );

                datesDirty = false;

                await updateWithEventDates($planData.plan.id);
            } else {
                await updatePlanDates(
                    $planData.plan.id,
                    $planData.plan.start,
                    $planData.plan.stop,
                    false,
                    []
                );
            }

            if (isFirstPageLoad) {
                $planData.plan.isActive = setVisibilityOnCreateNewPlan();
            }
        }

        showToFromDateTimePickerModal = false;
        setSectionFocus(planPageDate);
    }

    function dateTimePickerCancel() {
        showToFromDateTimePickerModal = false;
        setSectionFocus(planPageDate);
    }

    // Update plan based on dates set by event time
    async function updateWithEventDates(planId) {
        const planUpdate = await getPlan(planId);

        $planData.plan.start = adjustToTimezone(new Date(planUpdate.start));
        $planData.plan.stop = adjustToTimezone(new Date(planUpdate.stop));
    }

    async function createPlan() {
        if (!isTitleEmpty) {
            createPlanInProgress = true;
            await Promise.all([
                postNewPlannerKpi(config.kpi.kpiMeasurementCreateNewPlan),
                setPlanActive(
                    $planData.plan.id,
                    $planData.plan.isActive,
                    config.activateElementsByPlan
                )
            ]);
            await setPlanOrderingWithRedirect();
        } else {
            onBlur();
            setSectionFocus("plan-page__title");
        }
    }

    // Show the create topic modal
    function openCreateTopicModal() {
        showCreateTopicModal = true;
        showAddTopicModal = false;
        showMoveResourcesModal = false;
    }

    // Show the add topic modal
    function openAddTopicModal() {
        showCreateTopicModal = false;
        showAddTopicModal = true;
        showMoveResourcesModal = false;
    }

    async function addTopic({ detail }) {
        showCreateTopicModal = false;
        showAddTopicModal = false;
        showMoveResourcesModal = false;

        await addToTopicActions(detail);
    }

    function continueTopic(detail) {
        selectedTopicId = detail.topicId;
        selectedTopicLabel = detail.topicLabel;
        showAddTopicModal = false;
        showMoveResourcesModal = true;
    }

    async function addToTopicActions(detail) {
        let planFolderParentChanged = false;

        if (config.assignToEnabled) {
            planFolderParentChanged = await setTopicFolderForPlanFolder($planData.plan.id, initialTopicId, detail.topicId);

            if (planFolderParentChanged) {
                await connectToTopic($planData.plan.id, detail.topicId);
            }
        }

        if (!planFolderParentChanged) {
            const { showMoveResources, folderId } = await handleTopicConnection($planData.plan.id, initialTopicId, detail.topicId, moveResourcesCandidate);

            if (showMoveResources) {
                continueTopic(detail);
            }

            if (detail.topicId) {
                setPlanResourceFolderId(folderId);
            }
        }

        if (detail.topicId) {
            const topic = await getTopicForPlan(detail.topicId);

            $planData.plan.topic = topic;
        }
    }

    async function createTopic({ detail }) {
        const newTopic = await createNewTopic(detail.title, detail.colour);

        showCreateTopicModal = false;
        const urlParams = new URLSearchParams(newTopic.refreshUrl);
        const newTopicId = urlParams.get("TopicId");
        const topicDetail = {
            topicId: newTopicId,
            topicLabel: detail.title,
            useMoveResources: true
        };

        const showMoveResources = false;

        if ($planData.plan.id && newTopicId) {
            let planFolderParentChanged = false;

            if (config.assignToEnabled) {
                planFolderParentChanged = await setTopicFolderForPlanFolder($planData.plan.id, initialTopicId, newTopicId);

                if (planFolderParentChanged) {
                    await connectToTopic($planData.plan.id, newTopicId);
                }
            }

            if (!planFolderParentChanged) {
                const result = await handleTopicConnection($planData.plan.id, initialTopicId, newTopicId, moveResourcesCandidate);

                if (result.showMoveResources) {
                    continueTopic(topicDetail);
                }

                setPlanResourceFolderId(result.folderId);
            }

            const topic = await getTopicForPlan(newTopicId);

            $planData.plan.topic = topic;

            await postNewPlannerKpi(config.kpi.kpiMeasurementCreateNewTopic);
        }

        if (!showMoveResources) {
            setSectionFocus(planPageTopic);
        }
    }

    function setPlanResourceFolderId(folderId) {
        if ($planData.plan.resources) {
            $planData.plan.resources.folderId = folderId;
        } else {
            $planData.plan.resources = { folderId };
        }
    }

    function cancelTopic() {
        showCreateTopicModal = false;
        showAddTopicModal = false;
        showMoveResourcesModal = false;

        setSectionFocus(planPageTopic);
    }

    async function moveElementsInTopicFolderForPlan() {
        showCreateTopicModal = false;
        showAddTopicModal = false;
        showMoveResourcesModal = false;
        await moveElementsInFolderForPlan($planData.plan.id, initialTopicId, selectedTopicId);

        setSectionFocus(planPageTopic);
    }

    function cancelMoveResources() {
        showCreateTopicModal = false;
        showAddTopicModal = false;
        showMoveResourcesModal = false;
    }

    async function removeTopic() {
        await removeTopicFromPlan($planData.plan.id);

        if (config.assignToEnabled) {
            setTopicFolderForPlanFolder($planData.plan.id, initialTopicId, 0);
        }

        const target = await getPlanLastInOrder();
        const sourcePlan = $planData.plan;

        if (sourcePlan.id && target.PlanId) {
            await dragAndDropPlanInLesson(sourcePlan.id, target.PlanId, null, true);
        }

        $planData.plan.topic = undefined;
        setSectionFocus(planPageTopic);
    }

    // Find the first focusable element in a plan page section (link or button)
    function setSectionFocus(sectionElement) {
        const mainElement = `.${sectionElement}`;

        if (mainElement) {
            const container = document.querySelector(mainElement);

            if (container) {
                const focusElement = container.querySelector(
                    "a[href]:not([disabled]), button:not([disabled])"
                );

                if (focusElement) {
                    focusElement.focus();
                }
            }
        }
    }

    // Return true if the plan is currently active
    function isPlanCurrent() {
        return (
            $planData.plan &&
            $planData.plan.start &&
            $planData.plan.stop &&
            $planData.plan.start <= timeNow &&
            $planData.plan.stop > timeNow
        );
    }

    // planIsActiveByDefault is always false, but it can be changed if activation is manual (set in "Default status for plan").
    // If activation is NOT manual and the plan has already started (start date set in the past), then the plan will be created as visible.
    function setVisibilityOnCreateNewPlan() {
        return config.isActivationTypeManually
            ? config.planIsActiveByDefault
            : isPlanCurrent()
              ? true
              : config.planIsActiveByDefault;
    }

    // Update the visibility section when dates change in an existing plan
    async function updatePlanVisibility(id) {
        if (!config.isActivationTypeManually) {
            const planUpdate = await getPlan(id);

            $planData.plan.isActive = planUpdate.isActive;
            $planData.plan.activeFromDT = adjustToTimezone(planUpdate.activeFromDT);
        }
    }

    async function deletePlanClicked(event) {
        if (event.detail.confirmed === true) {
            await removePlanFromCourse($planData.plan.id);

            showDeletePlanConfirmModal = false;
            isPlanDeleted = true;

            window.location.href = $backLink.url;
        }

        // Cancel clicked
        showDeletePlanConfirmModal = false;
    }

    async function onDropDownOptionSelected(event) {
        const data = event.detail.event.detail.data;

        if (data.label == i18n.pinPlan) {
            pinnedPlan = await getPinnedPlan();

            if (pinnedPlan.PlanId === 0) {
                await updatePlanPinned($planData.plan.id, true);
                $planData.plan.pinned = true;
            } else {
                anotherPlanIsPinnedMessage = i18n.anotherPlanIsPinnedMessage.replace(
                    "{0}",
                    pinnedPlan.Name
                );
                showAlreadyPinnedPlanModal = true;
            }
        } else if (data.label == i18n.unpinPlan) {
            await updatePlanPinned($planData.plan.id, false);
            $planData.plan.pinned = false;
        }
    }

    async function planPinningModuleClicked(event) {
        if (event.detail.confirmed === true) {
            await updatePlanPinned($planData.plan.id, true);
            $planData.plan.pinned = true;
        }
    }

    async function setPlanOrderingWithRedirect() {
        const urlHasTopic = hasParam($backLink.url, TOPIC_ID_DESTINATION_QUERY_STRING_PARAMETER);
        const tableViewModeInUrl = getParam($backLink.url, TABLE_VIEW_MODE_QUERY_STRING_PARAMETER);
        const isTopicTab = (urlHasTopic || ($planData.plan.topic && $planData.plan.topic.id > 0)) && tableViewModeInUrl == tableViewMode.topics;
        const backLinkUrl = setParam(
            $backLink.url,
            TABLE_VIEW_MODE_QUERY_STRING_PARAMETER,
            isTopicTab ? tableViewMode.topics : tableViewMode.plans
        );
        const backLinkUrlWithCreatedPlanId = setParam(backLinkUrl, 'createdPlanId', $planData.plan.id);

        window.location.href = isTopicTab ? backLinkUrlWithCreatedPlanId : backLinkUrl;
    }

    function checkIfEmptyTitle() {
        const plansTextArea = document.querySelector(`${plansTextAreaElement}`);

        if (plansTextArea) {
            if (plansTextArea.value.length > 0) {
                isTitleEmpty = false;
            } else {
                isTitleEmpty = true;
            }
        }
    }

    function onBlur() {
        const plansPageTitle = document.querySelector(`${plansPageTitleElement}`);
        const plansTextArea = document.querySelector(`${plansTextAreaElement}`);

        if (plansTextArea && plansPageTitle) {
            if (plansTextArea.value.length === 0) {
                plansPageTitle.classList.add(`${plansPageTitleClass}`);
                showMandatoryFieldMessage = true;
                warningMessage = formatLanguageString(i18n.mandatoryField_Message, planTitleDisplayText);
            }

            if (plansTextArea.value.length > maxTitleLength) {
                plansPageTitle.classList.add(plansPageTitleClass);
                showMandatoryFieldMessage = true;
                warningMessage = formatLanguageString(i18n.titleLengthExceeded_Message, planTitleDisplayText);
            }
        }
    }

    function removeClass() {
        const plansPageTitle = document.querySelector(`${plansPageTitleElement}`);

        if (plansPageTitle) {
            plansPageTitle.classList.remove(`${plansPageTitleClass}`);
            showMandatoryFieldMessage = false;
        }
    }

    addEventListener("beforeunload", event => {
        if (window.plansConfig.newPlan && !createPlanInProgress && !isPlanDeleted) {
            event.preventDefault();
        }
    });

    const planActivationChanged = ({ detail: { elementsUpdated, elements } }) => {
        if (config.newPlan) {
            isFirstPageLoad = false;
            /* setPlanActive for a new plan is called in createNewPlan,
            so the plan will not be visible to students before the teacher presses "Create new".*/
        }

        if (elementsUpdated) {
            $planData.plan.elements = elements;
        }

        showDeletePlanConfirmModal = false;
    };

    const assignToChanged = ({ detail: { updatedElements } }) => {
        if (updatedElements && updatedElements.length > 0) {
            $planData.plan.elements = updatedElements;
        }

;
    };
</script>
